import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { DataManager, Visualisation, Toolbar, FilterBar } from 'wc-visualisation';
import { Table, Icon, Popover, formatDate, FieldType, downloadCsv, arrayToCsv } from 'wc-lib';
import * as screenfull from 'screenfull';
import { sortCsv } from '../../../helpers/stringHelper';
import { fixCurrencyFieldsForTable, fixDecimalColumnsForTable } from './dataUtils';
// import { FPP_FCAS_SUMMARY_1HR_testData } from './test-data/FPP_FCAS_SUMMARY_1HR-test-data';
// import { FPP_FCAS_SUMMARY_3HR_testData } from './test-data/FPP_FCAS_SUMMARY_3HR-test-data';
// import { FPP_FCAS_SUMMARY_24HR_testData } from './test-data/FPP_FCAS_SUMMARY_24HR-test-data';

require('wc-lib/icons/downloadcircle.svg');
require('wc-lib/icons/fullscreen.svg');

@Component({
  template: require('./fpp-fcas-summary.html'),
  name: 'fpp-fcas-summary',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'wc-table': Table,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class FppFcasSummary extends Vue {
  selectedInterval: string = '';
  intervals = [{ value: '1HR', label: '1 hr' }, { value: '3HR', label: '3 hrs' }, { value: '24HR', label: '24 hrs' }]

  dataCache: Record<string, any[]> = {};
  tableData: any[] = [];
  tableColumns: any[] = [];
  csvData: any[] = [];
  csvColumns = [
    {
      title: 'Interval Date Time',
      field: 'INTERVAL_DATETIME',
      formatter: (value) => {
        let date = value.INTERVAL_DATETIME || value;
        return formatDate(date, 'dd/MM/yyyy HH:mm');
      },
      sortable: true
    },
    {
      title: 'Constraint ID',
      field: 'CONSTRAINTID',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Run #',
      field: 'RUNNO',
      fieldType: FieldType.Number,
      sortable: true
    },
    {
      title: 'Version #',
      field: 'VERSIONNO',
      fieldType: FieldType.Number,
      sortable: true
    },
    {
      title: 'Bid Type',
      field: 'BIDTYPE',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Relevant Regions',
      field: 'RELEVANT_REGIONS',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Regulation MW',
      field: 'REGULATION_MW',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'Constraint Marginal Value ($/MW)',
      field: 'CONSTRAINT_MARGINAL_VALUE',
      fieldType: FieldType.Dollar,
      sortable: true
    },
    {
      title: 'P-regulation ($/MW)',
      field: 'P_REGULATION',
      fieldType: FieldType.Dollar,
      sortable: true
    },
    {
      title: 'Base Cost ($)',
      field: 'BASE_COST',
      fieldType: FieldType.Dollar,
      sortable: true
    },
    {
      title: 'TSFCAS ($)',
      field: 'TSFCAS',
      fieldType: FieldType.Dollar,
      sortable: true
    },
    {
      title: 'RCR (MW)',
      field: 'RCR',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'Usage',
      field: 'USAGE',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'Total FPP ($)',
      field: 'TOTAL_FPP',
      fieldType: FieldType.Dollar,
      sortable: true
    },
  ];
  loadMessage: string = '';

  intervalRefresh: any;

  created() {
    if (screenfull.isEnabled) {
      (screenfull as any).onchange(() => {
        this.isFullscreen = (screenfull as any).isFullscreen;
      });
    }

    this.selectedInterval = this.intervals[0].value;
    this.loadData(this.selectedInterval, true);

    this.intervalRefresh = setInterval(() => {
      this.loadData(this.selectedInterval, true);
    }, 300 * 1000);
  }

  destroyed() {
    clearInterval(this.intervalRefresh)
  }
  
  @Watch('selectedInterval')
  onSelectedIntervalChange(newValue: string, oldValue: string): void {
    this.selectedInterval = newValue;
    this.loadData(newValue, false);
  }

  loadData(interval: string, clearDataCache: boolean) {
    this.loadMessage = 'Loading data';
    this.fetchDataAsync(interval, clearDataCache).then(
      (data) => {
        this.tableData = fixCurrencyFieldsForTable(data, ['CONSTRAINT_MARGINAL_VALUE', 'P_REGULATION', 'BASE_COST', 'TSFCAS', 'TOTAL_FPP']);
        this.tableColumns = fixDecimalColumnsForTable(this.csvColumns, 5);
        this.csvData = data;
        this.loadMessage = '';
      },
      (error) => {
        this.loadMessage = 'No data found';
      });
  }

  async fetchDataAsync(interval: string, clearDataCache: boolean): Promise<any[]> {
    if (clearDataCache) {
      this.dataCache = {};
    }

    let reportName = `FPP_FCAS_SUMMARY_${interval}`;

    if (!this.dataCache[reportName]) {
      let id = await DataManager.addEndPoint('../api/report/' + reportName, undefined, 60000, reportName, undefined, 'yyyy-MM-ddTHH:mm:ss', ['INTERVAL_DATETIME', 'RUN_DATETIME']);
      let data = DataManager.getDynamicView(id, reportName).data();

      // let data: any[];
      // if (interval === '1HR') data = FPP_FCAS_SUMMARY_1HR_testData;
      // else if (interval === '3HR') data = FPP_FCAS_SUMMARY_3HR_testData;
      // else if (interval === '24HR') data = FPP_FCAS_SUMMARY_24HR_testData;
      // data = data.map((x) => ({ ...x, INTERVAL_DATETIME: new Date(Date.parse(x.INTERVAL_DATETIME)) }));

      this.dataCache[reportName] = data
        .map((x) => ({ ...x, RELEVANT_REGIONS: sortCsv(x.RELEVANT_REGIONS) })); // fix unsorted RELEVANT_REGIONS column
    }

    return this.dataCache[reportName];
  }

  //
  // table
  //

  download() {
    let rows = this.csvData;
    let columns = this.csvColumns;
    let csv = arrayToCsv(rows, columns);
    downloadCsv(`FPP_FCAS_SUMMARY_${this.selectedInterval}_${formatDate(rows[0].INTERVAL_DATETIME, 'yyyyMMddHHmm')}.csv`, csv);
  }

  //
  // full screen
  //

  isFullscreen = false;

  toggleFullscreen() {
    (screenfull as any).toggle();
    setTimeout(() => {
      if (!this.isFullscreen) {
        this.loadData(this.selectedInterval, false);
      }
    }, 2000); // delay gives screen.isFullscreen time to update after screen.toggle()
  }
}


// WEBPACK FOOTER //
// src/views/electricity/fpp/fpp-fcas-summary.ts